const MINIMUM_TOC_HEADINGS = 3;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function positionCurrentHorizontalLink(scroller) {
  const currentLink = scroller?.querySelector('[aria-current="page"]');
  updateHorizontalScrollerState(scroller);
  if (!currentLink || scroller.scrollWidth <= scroller.clientWidth) return;

  const scrollerRect = scroller.getBoundingClientRect();
  const linkRect = currentLink.getBoundingClientRect();
  const isFullyVisible = linkRect.left >= scrollerRect.left && linkRect.right <= scrollerRect.right;
  if (isFullyVisible) return;

  // Geometry-based movement works for both LTR and RTL scrollLeft implementations.
  const delta = linkRect.left + linkRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);
  const reduceMotion = window.matchMedia?.(REDUCED_MOTION_QUERY).matches;
  scroller.scrollBy({ left: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
}

function updateHorizontalScrollerState(scroller) {
  if (!scroller) return;

  const overflowAmount = scroller.scrollWidth - scroller.clientWidth;
  const isOverflowing = overflowAmount > 1;
  const scrollPosition = Math.abs(scroller.scrollLeft);

  scroller.classList.toggle('is-overflowing', isOverflowing);
  scroller.classList.toggle('is-at-start', !isOverflowing || scrollPosition <= 2);
  scroller.classList.toggle('is-at-end', !isOverflowing || scrollPosition >= overflowAmount - 2);
}

function slugifyHeading(text) {
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'policy-section';
}

function uniqueHeadingId(text, usedIds) {
  const baseId = slugifyHeading(text);
  let candidate = baseId;
  let suffix = 2;

  while (usedIds.has(candidate)) {
    candidate = `${baseId}-${suffix}`;
    suffix += 1;
  }

  usedIds.add(candidate);
  return candidate;
}

function createTocAnchor(entry) {
  const anchor = document.createElement('a');
  anchor.href = `#${encodeURIComponent(entry.id)}`;
  anchor.textContent = entry.text;
  anchor.dataset.policyTocTarget = entry.id;
  return anchor;
}

function buildTocList(entries) {
  const list = document.createElement('ul');
  let currentParent = null;

  entries.forEach((entry) => {
    const item = document.createElement('li');
    item.append(createTocAnchor(entry));

    if (entry.level === 3 && currentParent) {
      let nestedList = currentParent.querySelector(':scope > ul');
      if (!nestedList) {
        nestedList = document.createElement('ul');
        currentParent.append(nestedList);
      }
      nestedList.append(item);
      return;
    }

    list.append(item);
    currentParent = entry.level === 2 ? item : null;
  });

  return list;
}

function linkifyBareEmails(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const emailPattern = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
  const textNodes = [];

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node.parentElement?.closest('a, script, style')) continue;
    if (emailPattern.test(node.nodeValue)) textNodes.push(node);
    emailPattern.lastIndex = 0;
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue;
    const fragment = document.createDocumentFragment();
    let previousIndex = 0;

    for (const match of text.matchAll(emailPattern)) {
      const index = match.index ?? 0;
      fragment.append(text.slice(previousIndex, index));

      const anchor = document.createElement('a');
      anchor.href = `mailto:${match[0]}`;
      anchor.textContent = match[0];
      fragment.append(anchor);
      previousIndex = index + match[0].length;
    }

    fragment.append(text.slice(previousIndex));
    node.replaceWith(fragment);
  });
}

class PolicyPageShell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.policyReady === 'true') return;
    this.dataset.policyReady = 'true';

    this.nativeOutput = this.querySelector('[data-policy-native]');
    this.policyBody = this.nativeOutput?.querySelector('.shopify-policy__body .rte');
    if (!this.nativeOutput || !this.policyBody) return;

    this.moveNativeTitle();
    this.wrapTables();
    linkifyBareEmails(this.policyBody);

    this.headingEntries = this.collectHeadings();
    if (this.headingEntries.length >= MINIMUM_TOC_HEADINGS) {
      this.buildTocs();
      this.observeSections();
    }

    this.updateHeaderOffset = this.updateHeaderOffset.bind(this);
    this.positionCurrentPolicyLink = this.positionCurrentPolicyLink.bind(this);
    this.handlePolicySwitcherScroll = this.handlePolicySwitcherScroll.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
    this.updateHeaderOffset();
    this.observeHeader();
    this.observePolicySwitcher();
    window.addEventListener('resize', this.updateHeaderOffset, { passive: true });
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    this.classList.add('is-enhanced');
    requestAnimationFrame(() => {
      this.positionCurrentPolicyLink();
      this.restoreHashPosition();
    });
  }

  disconnectedCallback() {
    this.sectionObserver?.disconnect();
    this.headerResizeObserver?.disconnect();
    this.policySwitcherResizeObserver?.disconnect();
    this.policySwitcher?.removeEventListener('scroll', this.handlePolicySwitcherScroll);
    if (this.policySwitcherPositionFrame) {
      window.cancelAnimationFrame(this.policySwitcherPositionFrame);
    }
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    window.removeEventListener('resize', this.updateHeaderOffset);
  }

  handleSectionLoad() {
    this.observeHeader();
    this.updateHeaderOffset();
    this.positionCurrentPolicyLink();
  }

  observePolicySwitcher() {
    const switcher = this.querySelector('.ow-policy-switcher');
    if (!switcher) return;

    this.policySwitcher?.removeEventListener('scroll', this.handlePolicySwitcherScroll);
    this.policySwitcher = switcher;
    this.policySwitcher.addEventListener('scroll', this.handlePolicySwitcherScroll, { passive: true });
    updateHorizontalScrollerState(this.policySwitcher);

    if (typeof window.ResizeObserver !== 'function') return;

    this.policySwitcherResizeObserver?.disconnect();
    this.policySwitcherResizeObserver = new window.ResizeObserver(this.positionCurrentPolicyLink);
    this.policySwitcherResizeObserver.observe(switcher);
  }

  handlePolicySwitcherScroll() {
    updateHorizontalScrollerState(this.policySwitcher);
  }

  positionCurrentPolicyLink() {
    const switcher = this.querySelector('.ow-policy-switcher');
    if (!switcher) return;

    if (this.policySwitcherPositionFrame) {
      window.cancelAnimationFrame(this.policySwitcherPositionFrame);
    }

    this.policySwitcherPositionFrame = window.requestAnimationFrame(() => {
      this.policySwitcherPositionFrame = window.requestAnimationFrame(() => {
        positionCurrentHorizontalLink(switcher);
        updateHorizontalScrollerState(switcher);
        this.policySwitcherPositionFrame = null;
      });
    });
  }

  moveNativeTitle() {
    const title = this.nativeOutput.querySelector('.shopify-policy__title');
    const titleSlot = this.querySelector('[data-policy-title]');
    if (title && titleSlot) titleSlot.append(title);
  }

  collectHeadings() {
    const usedIds = new Set(
      [...document.querySelectorAll('[id]')]
        .map((element) => element.id)
        .filter(Boolean)
    );

    return [...this.policyBody.querySelectorAll('h2, h3')].flatMap((heading) => {
      const text = heading.textContent.replace(/\u00a0/g, ' ').trim();
      if (!text) {
        heading.classList.add('is-empty-policy-heading');
        heading.setAttribute('aria-hidden', 'true');
        return [];
      }

      if (!heading.id) heading.id = uniqueHeadingId(text, usedIds);

      return [{
        element: heading,
        id: heading.id,
        level: Number(heading.tagName.slice(1)),
        text,
      }];
    });
  }

  buildTocs() {
    const desktopToc = this.querySelector('[data-policy-toc-desktop]');
    const mobileToc = this.querySelector('[data-policy-toc-mobile]');

    this.querySelectorAll('[data-policy-toc-list]').forEach((container) => {
      container.replaceChildren(buildTocList(this.headingEntries));
    });

    desktopToc?.removeAttribute('hidden');
    mobileToc?.removeAttribute('hidden');
    mobileToc?.addEventListener('click', (event) => {
      if (event.target.closest('a')) mobileToc.open = false;
    });
  }

  observeSections() {
    if (typeof window.IntersectionObserver !== 'function') {
      this.setActiveSection(this.headingEntries[0].id);
      return;
    }

    this.sectionObserver = new window.IntersectionObserver(
      () => {
        const threshold = Math.max(96, window.innerHeight * 0.28);
        const passed = this.headingEntries.filter(
          (entry) => entry.element.getBoundingClientRect().top <= threshold
        );
        const activeEntry = passed[passed.length - 1] || this.headingEntries[0];
        this.setActiveSection(activeEntry.id);
      },
      { rootMargin: '-18% 0px -70% 0px', threshold: 0 }
    );

    this.headingEntries.forEach((entry) => this.sectionObserver.observe(entry.element));
    this.setActiveSection(this.headingEntries[0].id);
  }

  setActiveSection(id) {
    this.querySelectorAll('[data-policy-toc-target]').forEach((anchor) => {
      if (anchor.dataset.policyTocTarget === id) {
        anchor.setAttribute('aria-current', 'location');
      } else {
        anchor.removeAttribute('aria-current');
      }
    });
  }

  wrapTables() {
    this.policyBody.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('ow-policy__table-scroll')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'ow-policy__table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      if (this.dataset.tableLabel) wrapper.setAttribute('aria-label', this.dataset.tableLabel);
      table.before(wrapper);
      wrapper.append(table);
    });
  }

  observeHeader() {
    this.headerResizeObserver?.disconnect();
    if (typeof window.ResizeObserver !== 'function') return;

    const headerElements = [
      document.querySelector('.header-mobile-overlay'),
      document.querySelector('#header-group .header-section'),
      document.querySelector('#header-component'),
    ].filter((element, index, elements) => element && elements.indexOf(element) === index);

    this.headerResizeObserver = new window.ResizeObserver(this.updateHeaderOffset);
    headerElements.forEach((element) => this.headerResizeObserver.observe(element));
  }

  updateHeaderOffset() {
    const mobileOverlay = document.querySelector('.header-mobile-overlay[data-mounted="body"]');
    const mobileOverlayHeight = this.getTopHeaderHeight(mobileOverlay, true);
    const headerHeight = mobileOverlayHeight || this.getDesktopHeaderHeight();

    this.style.setProperty('--ow-policy-sticky-top', `${Math.ceil(headerHeight + 24)}px`);
    this.style.setProperty('--ow-policy-scroll-offset', `${Math.ceil(headerHeight + 32)}px`);
  }

  getTopHeaderHeight(element, allowFixedOverlay = false) {
    if (!(element instanceof HTMLElement)) return 0;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return 0;

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return 0;

    const isTopLayer = allowFixedOverlay || style.position === 'fixed' || style.position === 'sticky';
    if (!isTopLayer || rect.top > 1 || rect.bottom <= 0) return 0;

    return Math.max(0, Math.min(rect.bottom, window.innerHeight));
  }

  getDesktopHeaderHeight() {
    const headerSection = document.querySelector('#header-group .header-section');
    const headerComponent = document.querySelector('#header-component');
    const visibleHeight = Math.max(
      this.getTopHeaderHeight(headerSection),
      this.getTopHeaderHeight(headerComponent)
    );

    if (visibleHeight > 0) return visibleHeight;

    const stickyMode = headerComponent?.getAttribute('sticky');
    const canBecomeSticky = stickyMode === 'always' || stickyMode === 'scroll-up';
    const componentHeight = headerComponent?.getBoundingClientRect().height || 0;
    if (canBecomeSticky && componentHeight > 0) return componentHeight;

    const bodyStyle = window.getComputedStyle(document.body);
    const configuredHeight = parseFloat(bodyStyle.getPropertyValue('--header-height')) || 0;
    const configuredGroupHeight = parseFloat(bodyStyle.getPropertyValue('--header-group-height')) || 0;
    return Math.max(configuredHeight, configuredGroupHeight);
  }

  restoreHashPosition() {
    if (!window.location.hash) return;

    let id;
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch (_error) {
      id = window.location.hash.slice(1);
    }

    document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }
}

if (!customElements.get('policy-page-shell')) {
  customElements.define('policy-page-shell', PolicyPageShell);
}
